// Centralized event bus built on top of window + CustomEvent.
// Features:
// - emit(eventName, detail, { sticky }): broadcast payloads (optionally remember the last one)
// - on(eventName, handler, { immediate, signal }): subscribe with optional immediate replay & auto-unsubscribe via AbortSignal
// - clearSticky(eventName): drop remembered payload
// - Chat-specific helpers: EVENT_CHAT_SEND, sendChatMessage(), onChatMessage()

/** Default event for chat message flow */
const EVENT_CHAT_SEND = 'chatty:sendI18nMessage';
const EVENT_CHAT_CALL_TOOL = 'chatty:callTool';

/** Stores the most recent sticky payload per event name */
const lastPayloadByEvent = new Map();

/** True when running in a browser context */
const hasWindow =
  typeof window !== 'undefined' &&
  typeof window.addEventListener === 'function' &&
  typeof window.dispatchEvent === 'function';

/** Microtask helper (queueMicrotask fallback for broader environments) */
function scheduleMicrotask(fn) {
  if (typeof queueMicrotask === 'function') return queueMicrotask(fn);
  Promise.resolve().then(fn);
}

/**
 * Emit a CustomEvent on window.
 *
 * @param {string} eventName - The event name (e.g. "chat:sendMessage")
 * @param {any} detail - Arbitrary payload to deliver to listeners (accessible as event.detail)
 * @param {{ sticky?: boolean }} [opts]
 *  - sticky: remember the last payload for this event and replay to late subscribers
 * @returns {boolean} true if dispatched; false if no window
 */
export function emit(eventName, detail, opts = {}) {
  const {sticky = false} = opts;
  if (!hasWindow) return false;

  if (typeof eventName !== 'string' || eventName.length === 0) {
    throw new Error('emit(eventName, ...): eventName must be a non-empty string.');
  }

  if (sticky) lastPayloadByEvent.set(eventName, detail);

  const evt = new CustomEvent(eventName, {detail});
  window.dispatchEvent(evt);
  return true;
}

/**
 * Subscribe to a CustomEvent on window.
 *
 * @param {string} eventName
 * @param {(detail: any, event: CustomEvent) => void} handler
 * @param {{ immediate?: boolean, signal?: AbortSignal }} [opts]
 *  - immediate: if a sticky payload exists, invoke handler immediately (via microtask)
 *  - signal: auto-unsubscribe when the provided AbortController is aborted
 * @returns {() => void} off() - call to remove the listener
 */
export function on(eventName, handler, opts = {}) {
  if (!hasWindow) return () => {};

  if (typeof eventName !== 'string' || eventName.length === 0) {
    throw new Error('on(eventName, ...): eventName must be a non-empty string.');
  }
  if (typeof handler !== 'function') {
    throw new Error('on(...): handler must be a function.');
  }

  const wrapped = e => {
    // Ensure we always pass (detail, event)
    handler(e?.detail, e);
  };

  window.addEventListener(eventName, wrapped);

  // Optional immediate replay of the last sticky payload
  if (opts.immediate && lastPayloadByEvent.has(eventName)) {
    const detail = lastPayloadByEvent.get(eventName);
    // Use a microtask to avoid invoking during render phases in frameworks like React
    scheduleMicrotask(() => handler(detail, new CustomEvent(eventName, {detail})));
  }

  const off = () => window.removeEventListener(eventName, wrapped);

  // Optional auto-off via AbortSignal
  if (opts.signal) {
    if (opts.signal.aborted) {
      off();
    } else {
      opts.signal.addEventListener('abort', off, {once: true});
    }
  }

  return off;
}

/**
 * Remove remembered sticky payload for an event.
 * @param {string} eventName
 */
export function clearSticky(eventName) {
  lastPayloadByEvent.delete(eventName);
}

/* =========================
 * Chat-focused convenience
 * ========================= */

/**
 * Emit a chat message event.
 * @param {{ text?: string, userId?: string, meta?: any, phrase?: * }} payload
 * @param {{ sticky?: boolean }} [opts]
 * @returns {boolean}
 */
export function sendChatMessage(payload, opts = {}) {
  return emit(EVENT_CHAT_SEND, payload, opts);
}

export function callTool(payload, opts) {
  return emit(EVENT_CHAT_CALL_TOOL, payload, opts);
}

/**
 * Subscribe to chat message events.
 * @param {(payload: { text: string, userId?: string, meta?: any }) => void} handler
 * @param {{ immediate?: boolean, signal?: AbortSignal }} [opts]
 * @param {string} key - The key to identify the event
 * @returns {() => void} off()
 */
export function onChatMessage(handler, opts, key = '') {
  return on(EVENT_CHAT_SEND + key, handler, opts);
}

export function onChatTool(handler, opts) {
  return on(EVENT_CHAT_CALL_TOOL, handler, opts);
}
